import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { db } from "@/lib/firebase";
import { 
  collection, 
  getDocs, 
  doc, 
  setDoc, 
  deleteDoc, 
  onSnapshot,
  serverTimestamp 
} from "firebase/firestore";
import { 
  Search, 
  UserCheck, 
  Heart, 
  Mail, 
  Phone, 
  MapPin,
  Star,
  Filter,
  X,
  Briefcase
} from "lucide-react";
import { BrokerSidebar } from "@/components/layout/BrokerSidebar";
import { DashboardHeader } from "@/components/layout/DashboardHeader";

interface Consultant {
  id: string;
  name: string;
  specialty: string;
  expertise?: string[];
  location?: string;
  email?: string;
  phone?: string;
  hourly_rate?: number;
  currency?: string;
  rating?: number;
  projects_completed?: number;
  availability?: string;
  bio?: string;
}

const fallbackConsultants: Consultant[] = [
  { 
    id: "c1", 
    name: "Sophie Dubois", 
    specialty: "Business Valuation", 
    expertise: ["M&A", "Due Diligence", "Financial Analysis"],
    location: "Montreal, QC", 
    email: "sophie@example.com",
    phone: "+1 514 555-0101",
    hourly_rate: 150,
    currency: "CAD",
    rating: 4.8,
    projects_completed: 45,
    availability: "available",
    bio: "15+ years experience in business valuations and M&A transactions."
  },
  { 
    id: "c2", 
    name: "Marc Tremblay", 
    specialty: "Corporate Law", 
    expertise: ["Contracts", "M&A", "Corporate Governance"],
    location: "Quebec City, QC", 
    email: "marc@example.com",
    phone: "+1 418 555-0102",
    hourly_rate: 200,
    currency: "CAD",
    rating: 4.9,
    projects_completed: 78,
    availability: "available",
    bio: "Corporate lawyer specialized in business acquisitions."
  },
  { 
    id: "c3", 
    name: "Nathalie Vachon", 
    specialty: "Tax Advisory", 
    expertise: ["Tax Planning", "M&A Tax", "Corporate Restructuring"],
    location: "Montreal, QC", 
    email: "nathalie@example.com",
    phone: "+1 514 555-0103",
    hourly_rate: 180,
    currency: "CAD",
    rating: 4.7,
    projects_completed: 62,
    availability: "busy",
    bio: "Tax advisor specializing in SME acquisitions and restructuring."
  },
  { 
    id: "c4", 
    name: "Pierre Gagnon", 
    specialty: "Financial Due Diligence", 
    expertise: ["Financial Analysis", "Risk Assessment", "Audit"],
    location: "Toronto, ON", 
    email: "pierre@example.com",
    phone: "+1 416 555-0104",
    hourly_rate: 175,
    currency: "CAD",
    rating: 4.6,
    projects_completed: 53,
    availability: "available",
    bio: "CPA with extensive experience in transaction due diligence."
  },
  { 
    id: "c5", 
    name: "Marie-Claire Beaumont", 
    specialty: "Immigration Law", 
    expertise: ["Business Immigration", "Work Permits", "PEQ"],
    location: "Montreal, QC", 
    email: "marie@example.com",
    phone: "+1 514 555-0105",
    hourly_rate: 190,
    currency: "CAD",
    rating: 4.8,
    projects_completed: 89,
    availability: "available",
    bio: "Immigration lawyer specializing in entrepreneur and investor immigration."
  },
];

const translations = {
  en: {
    title: "My Consultants",
    subtitle: "Find and manage your professional advisors",
    searchPlaceholder: "Search by name, specialty, or location...",
    allSpecialties: "All Specialties",
    allAvailability: "All Availability",
    available: "Available",
    busy: "Busy",
    unavailable: "Unavailable",
    noConsultants: "No consultants found matching your criteria.",
    hourlyRate: "Hourly Rate",
    projectsCompleted: "Projects",
    contact: "Contact",
    favorite: "Favorite",
    unfavorite: "Unfavorite",
    addedToFavorites: "Added to favorites",
    removedFromFavorites: "Removed from favorites",
    filters: "Filters",
    clearFilters: "Clear Filters",
    expertise: "Expertise",
  },
  fr: {
    title: "Mes Consultants",
    subtitle: "Trouvez et gérez vos conseillers professionnels",
    searchPlaceholder: "Rechercher par nom, spécialité ou lieu...",
    allSpecialties: "Toutes les spécialités",
    allAvailability: "Toutes disponibilités",
    available: "Disponible",
    busy: "Occupé",
    unavailable: "Non disponible",
    noConsultants: "Aucun consultant trouvé correspondant à vos critères.",
    hourlyRate: "Tarif horaire",
    projectsCompleted: "Projets",
    contact: "Contacter",
    favorite: "Favori",
    unfavorite: "Retirer",
    addedToFavorites: "Ajouté aux favoris",
    removedFromFavorites: "Retiré des favoris",
    filters: "Filtres",
    clearFilters: "Effacer les filtres",
    expertise: "Expertise",
  },
};

export default function BrokerConsultants() {
  const { language } = useLanguage();
  const { user } = useAuth();
  const { toast } = useToast();
  const t = translations[language as keyof typeof translations] || translations.en;

  const [consultants, setConsultants] = useState<Consultant[]>([]);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [specialtyFilter, setSpecialtyFilter] = useState<string>("all");
  const [availabilityFilter, setAvailabilityFilter] = useState<string>("all");
  const [activeTab, setActiveTab] = useState("consultants");

  // Load consultants
  useEffect(() => {
    const loadConsultants = async () => {
      try {
        const snap = await getDocs(collection(db, "consultants"));
        if (snap.empty) {
          setConsultants(fallbackConsultants);
        } else {
          const list: Consultant[] = snap.docs.map((d) => ({ 
            id: d.id, 
            ...(d.data() as Omit<Consultant, 'id'>) 
          }));
          setConsultants(list);
        }
      } catch {
        setConsultants(fallbackConsultants);
      } finally {
        setLoading(false);
      }
    };
    loadConsultants();
  }, []);

  // Listen to favorites
  useEffect(() => {
    if (!user?.id) return;
    const favCol = collection(db, "users", user.id, "consultantsFavorites");
    const unsub = onSnapshot(favCol, (snap) => {
      const ids = new Set<string>();
      snap.forEach((d) => ids.add(d.id));
      setFavorites(ids);
    });
    return () => unsub();
  }, [user?.id]);

  const toggleFavorite = async (consultantId: string) => {
    if (!user?.id) return;
    const favRef = doc(db, "users", user.id, "consultantsFavorites", consultantId);
    const isFavorite = favorites.has(consultantId);
    
    try {
      if (isFavorite) {
        await deleteDoc(favRef);
        toast({
          title: t.removedFromFavorites,
          variant: "default",
        });
      } else {
        await setDoc(favRef, { addedAt: serverTimestamp() });
        toast({
          title: t.addedToFavorites,
          variant: "default",
        });
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
    }
  };

  const contactConsultant = async (consultant: Consultant) => {
    if (!user?.id) return;
    try {
      const reqRef = doc(collection(db, "contactRequests"));
      await setDoc(reqRef, {
        type: "consultant",
        consultantId: consultant.id,
        consultantName: consultant.name,
        fromUserId: user.id,
        status: "pending",
        createdAt: serverTimestamp(),
      });
      toast({
        title: language === 'fr' ? "Demande envoyée ✅" : "Request sent ✅",
        description: language === 'fr' 
          ? `Demande de contact envoyée à ${consultant.name}` 
          : `Contact request sent to ${consultant.name}`,
      });
    } catch (error) {
      console.error("Error sending contact request:", error);
    }
  };

  // Get unique specialties for filter
  const specialties = useMemo(() => {
    const specs = new Set<string>();
    consultants.forEach(c => specs.add(c.specialty));
    return Array.from(specs).sort();
  }, [consultants]);

  // Filtered consultants
  const filteredConsultants = useMemo(() => {
    const q = search.trim().toLowerCase();
    return consultants.filter((c) => {
      const matchesSearch = !q || 
        c.name.toLowerCase().includes(q) ||
        c.specialty.toLowerCase().includes(q) ||
        c.location?.toLowerCase().includes(q) ||
        c.expertise?.some(e => e.toLowerCase().includes(q));
      
      const matchesSpecialty = specialtyFilter === "all" || c.specialty === specialtyFilter;
      const matchesAvailability = availabilityFilter === "all" || c.availability === availabilityFilter;
      
      return matchesSearch && matchesSpecialty && matchesAvailability;
    });
  }, [search, specialtyFilter, availabilityFilter, consultants]);

  const clearFilters = () => {
    setSearch("");
    setSpecialtyFilter("all");
    setAvailabilityFilter("all");
  };

  const hasActiveFilters = search || specialtyFilter !== "all" || availabilityFilter !== "all";

  const getAvailabilityBadge = (availability?: string) => {
    switch (availability) {
      case "available":
        return <Badge className="bg-green-500/10 text-green-600 border-green-500/20">{t.available}</Badge>;
      case "busy":
        return <Badge className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20">{t.busy}</Badge>;
      default:
        return <Badge variant="secondary">{t.unavailable}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex">
        <BrokerSidebar activeTab={activeTab} onTabChange={setActiveTab} />
        <div className="flex-1 flex flex-col">
          <DashboardHeader />
          <main className="flex-1 p-6">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-muted rounded w-1/4" />
              <div className="h-12 bg-muted rounded" />
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1,2,3].map(i => <div key={i} className="h-64 bg-muted rounded" />)}
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex">
      <BrokerSidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <div className="flex-1 flex flex-col">
        <DashboardHeader />
        <main className="flex-1 p-6 overflow-auto">
          <div className="space-y-6">
            {/* Header */}
            <div>
              <h1 className="text-3xl font-bold tracking-tight">{t.title}</h1>
              <p className="text-muted-foreground mt-2">{t.subtitle}</p>
            </div>

            {/* Filters */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder={t.searchPlaceholder}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  
                  <Select value={specialtyFilter} onValueChange={setSpecialtyFilter}>
                    <SelectTrigger className="w-full md:w-[200px]">
                      <SelectValue placeholder={t.allSpecialties} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t.allSpecialties}</SelectItem>
                      {specialties.map(spec => (
                        <SelectItem key={spec} value={spec}>{spec}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={availabilityFilter} onValueChange={setAvailabilityFilter}>
                    <SelectTrigger className="w-full md:w-[180px]">
                      <SelectValue placeholder={t.allAvailability} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t.allAvailability}</SelectItem>
                      <SelectItem value="available">{t.available}</SelectItem>
                      <SelectItem value="busy">{t.busy}</SelectItem>
                      <SelectItem value="unavailable">{t.unavailable}</SelectItem>
                    </SelectContent>
                  </Select>

                  {hasActiveFilters && (
                    <Button variant="ghost" onClick={clearFilters} className="gap-2">
                      <X className="h-4 w-4" />
                      {t.clearFilters}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Results */}
            {filteredConsultants.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <UserCheck className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">{t.noConsultants}</p>
                  {hasActiveFilters && (
                    <Button variant="outline" onClick={clearFilters} className="mt-4">
                      {t.clearFilters}
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredConsultants.map((consultant) => (
                  <Card key={consultant.id} className="hover:shadow-md transition-shadow">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                            <UserCheck className="h-6 w-6 text-primary" />
                          </div>
                          <div>
                            <CardTitle className="text-lg">{consultant.name}</CardTitle>
                            <CardDescription>{consultant.specialty}</CardDescription>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => toggleFavorite(consultant.id)}
                          className={favorites.has(consultant.id) ? "text-red-500" : "text-muted-foreground"}
                        >
                          <Heart className={`h-5 w-5 ${favorites.has(consultant.id) ? "fill-current" : ""}`} />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between">
                        {getAvailabilityBadge(consultant.availability)}
                        {consultant.rating && (
                          <div className="flex items-center gap-1 text-sm">
                            <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                            <span>{consultant.rating}</span>
                          </div>
                        )}
                      </div>

                      {consultant.bio && (
                        <p className="text-sm text-muted-foreground line-clamp-2">{consultant.bio}</p>
                      )}

                      {consultant.expertise && consultant.expertise.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {consultant.expertise.slice(0, 3).map((exp, i) => (
                            <Badge key={i} variant="outline" className="text-xs">
                              {exp}
                            </Badge>
                          ))}
                        </div>
                      )}

                      <div className="space-y-2 text-sm text-muted-foreground">
                        {consultant.location && (
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4" />
                            <span>{consultant.location}</span>
                          </div>
                        )}
                        {consultant.hourly_rate && (
                          <div className="flex items-center gap-2">
                            <Briefcase className="h-4 w-4" />
                            <span>{t.hourlyRate}: ${consultant.hourly_rate} {consultant.currency || 'CAD'}/h</span>
                          </div>
                        )}
                        {consultant.projects_completed && (
                          <div className="flex items-center gap-2">
                            <Star className="h-4 w-4" />
                            <span>{t.projectsCompleted}: {consultant.projects_completed}</span>
                          </div>
                        )}
                      </div>

                      <div className="flex gap-2 pt-2">
                        {consultant.email && (
                          <Button variant="outline" size="sm" className="flex-1" asChild>
                            <a href={`mailto:${consultant.email}`}>
                              <Mail className="h-4 w-4 mr-1" />
                              Email
                            </a>
                          </Button>
                        )}
                        <Button size="sm" className="flex-1" onClick={() => contactConsultant(consultant)}>
                          <Phone className="h-4 w-4 mr-1" />
                          {t.contact}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
